import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  View,
  StyleSheet,
  Image,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  Platform,
  ScrollView,
  Text,
} from "react-native";
import Ionicons from "react-native-vector-icons/Ionicons";
import { Arrow, Download } from "../../../../assets";
import NormalText from "../../../common/native/NormalText";
import PrimaryButton from "../../../common/native/PrimaryButton";
import { createUUID } from "../../../../utils/functions";

export type ImageCarouselProps = {
  images: string[];
  thumbnailUrls?: string[];
  initialIndex?: number;
  showDownloadAllButton?: boolean;
  showDownloadOneButton?: boolean;
  showDeleteButton?: boolean;
  showPaginationDots?: boolean;
  showpPaginationDots?: boolean; // Keeping this for backward compatibility
  loading?: boolean;
  error?: string;
  patientName?: string;
  onImageView?: (index: number) => void;
  onDownloadAll?: () => void;
  onDownloadOne?: (imageIndex: number) => void;
  onDeleteOne?: (imageIndex: number) => void;
  onImageChange?: (newIndex: number) => void;
  rotations?: number[]; // Add rotation angles array
  paginationDotThreshold?: number;
  showThumbnailDrawer?: boolean;
};

const DOWNLOAD_BUTTON_TEXT = "Download All";
const DOWNLOADED_BUTTON_TEXT = "Downloaded";
const DEFAULT_PAGINATION_DOT_THRESHOLD = 10;

const tokens = {
  color: {
    text: "#17191D",
    muted: "#64748B",
    surface: "#FFFFFF",
    surfaceSubtle: "#F8FAFC",
    border: "#E2E8F0",
    borderMuted: "#CBD5E1",
    primary: "#00B7F4",
    primarySubtle: "#E0F7FE",
    dangerSurface: "rgba(0,0,0,0.5)",
    transparent: "transparent",
    shadow: "#000",
    loading: "#00B7E0",
    error: "#DC2626",
  },
  spacing: {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 20,
    xxl: 24,
  },
  radius: {
    sm: 4,
    md: 8,
    lg: 12,
    round: 22,
  },
  shadow: {
    opacity: 0.3,
    radius: 6,
    elevation: 8,
  },
  font: {
    base: "Urbanist600",
    medium: "Urbanist500",
  },
  fontSize: {
    xs: 12,
    sm: 14,
    md: 16,
  },
  lineHeight: {
    sm: 20,
    md: 24,
  },
  size: {
    minTouchTarget: 44,
    compactNavigationMaxWidth: 760,
    navigationButtonMinWidth: 44,
    counterMinWidth: 112,
    viewAllButtonMinWidth: 96,
    thumbnailDrawerMaxHeight: 190,
    drawerThumbnail: 88,
    thumbnailBadge: 24,
  },
  zIndex: {
    base: 1,
  },
  motion: {
    microMs: 100,
    standardMs: 200,
    complexMs: 300,
  },
};

const getUrlHost = (url: string) => {
  try {
    return new URL(url).host;
  } catch {
    return "invalid-url";
  }
};

const logDownloadDiagnostics = (
  label: string,
  url: string,
  response?: Response,
  error?: unknown,
) => {
  const host = getUrlHost(url);
  if (response) {
    console.info(`[DownloadDebug:${label}]`, {
      host,
      requestUrl: url,
      finalUrl: response.url,
      status: response.status,
      ok: response.ok,
      contentDisposition: response.headers.get("content-disposition"),
      contentType: response.headers.get("content-type"),
      accessControlAllowOrigin: response.headers.get(
        "access-control-allow-origin",
      ),
      accessControlExposeHeaders: response.headers.get(
        "access-control-expose-headers",
      ),
    });
    return;
  }

  console.warn(`[DownloadDebug:${label}:error]`, {
    host,
    requestUrl: url,
    error: error instanceof Error ? error.message : String(error),
  });
};

const getDownloadFilename = (url: string) => {
  const fallbackExtension = ".png";
  try {
    const pathname = new URL(url).pathname;
    const extensionMatch = pathname.match(/\.[A-Za-z0-9]+$/);
    const extension = extensionMatch?.[0] || fallbackExtension;
    return `${createUUID()}${extension}`;
  } catch {
    return `${createUUID()}${fallbackExtension}`;
  }
};

const triggerBlobDownload = (blob: Blob, filename: string) => {
  if (typeof document === "undefined") return false;
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
  return true;
};

const triggerDirectDownloadFallback = (url: string) => {
  if (typeof document === "undefined") return false;
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", getDownloadFilename(url));
  link.setAttribute("rel", "noopener noreferrer");
  document.body.appendChild(link);
  link.click();
  link.remove();
  return true;
};

const toSafeIndex = (index: number, length: number) => {
  if (length <= 0) return 0;
  const safeInitialIndex = Number.isFinite(index) ? index : 0;
  return Math.min(Math.max(safeInitialIndex, 0), length - 1);
};

const ImageCarousel: React.FC<ImageCarouselProps> = ({
  images,
  thumbnailUrls = [],
  initialIndex = 0,
  showDownloadAllButton = false,
  showDownloadOneButton = false,
  showDeleteButton = false,
  showpPaginationDots = true,
  showPaginationDots = true,
  loading = false,
  error = "",
  onImageView,
  onDownloadAll,
  onDownloadOne,
  onDeleteOne,
  onImageChange,
  rotations = [], // Default to empty array
  paginationDotThreshold = DEFAULT_PAGINATION_DOT_THRESHOLD,
  showThumbnailDrawer = false,
}) => {
  const [currentIndex, setCurrentIndex] = useState(() =>
    toSafeIndex(initialIndex, images.length),
  );
  const [buttonText, setButtonText] = useState<string>(DOWNLOAD_BUTTON_TEXT);
  const [isThumbnailDrawerOpen, setIsThumbnailDrawerOpen] = useState(false);
  const [failedThumbnailIndexes, setFailedThumbnailIndexes] = useState<
    Record<number, boolean>
  >({});
  const onImageChangeRef = useRef<typeof onImageChange>(onImageChange);
  const onImageViewRef = useRef<typeof onImageView>(onImageView);
  const lastNotifiedIndexRef = useRef<number | null>(null);
  const imageSignature = useMemo(() => images.join("|"), [images]);
  const thumbnailUrlSignature = useMemo(
    () => thumbnailUrls.join("|"),
    [thumbnailUrls],
  );
  const shouldUseCompactNavigation =
    images.length > Math.max(paginationDotThreshold, 0);
  const shouldShowThumbnailDrawer =
    showThumbnailDrawer && shouldUseCompactNavigation;
  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex < images.length - 1;

  useEffect(() => {
    onImageChangeRef.current = onImageChange;
  }, [onImageChange]);

  useEffect(() => {
    onImageViewRef.current = onImageView;
  }, [onImageView]);

  useEffect(() => {
    setCurrentIndex(toSafeIndex(initialIndex, images.length));
    setIsThumbnailDrawerOpen(false);
    setFailedThumbnailIndexes({});
  }, [initialIndex, images.length, imageSignature, thumbnailUrlSignature]);

  // Notify parent only when index actually changes (not when callback identity changes).
  useEffect(() => {
    if (lastNotifiedIndexRef.current === currentIndex) return;
    lastNotifiedIndexRef.current = currentIndex;

    if (
      onImageChangeRef.current &&
      typeof onImageChangeRef.current === "function"
    ) {
      onImageChangeRef.current(currentIndex);
    }
    if (
      onImageViewRef.current &&
      typeof onImageViewRef.current === "function"
    ) {
      onImageViewRef.current(currentIndex);
    }
  }, [currentIndex]);

  const handleNext = () => {
    if (currentIndex < images.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const handleSelectImage = (index: number) => {
    setCurrentIndex(index);
  };

  const handleOnClickDownloadAll = async () => {
    if (Platform.OS !== "web") {
      Alert.alert("Error", "Image download is only available on web.");
      return;
    }

    setButtonText(DOWNLOADED_BUTTON_TEXT);
    try {
      if (onDownloadAll && typeof onDownloadAll === "function") {
        onDownloadAll();
      } else {
        const triggerDownload = async (url: string) => {
          const filename = getDownloadFilename(url);
          try {
            const response = await fetch(url);
            logDownloadDiagnostics("all.fetch", url, response);
            if (!response.ok) throw new Error("Failed to fetch image");
            const blob = await response.blob();
            const blobDownloaded = triggerBlobDownload(blob, filename);
            if (!blobDownloaded) {
              throw new Error("Unable to trigger blob download");
            }
          } catch (error) {
            logDownloadDiagnostics("all.fetch", url, undefined, error);
            const fallbackTriggered = triggerDirectDownloadFallback(url);
            if (!fallbackTriggered) {
              throw new Error("Unable to trigger fallback download");
            }
          }
        };

        for (const url of images) {
          await triggerDownload(url);
        }
      }
    } catch (error) {
      Alert.alert("Error", "Unable to download image.");
    }

    setTimeout(() => {
      setButtonText(DOWNLOAD_BUTTON_TEXT);
    }, 2000);
  };

  const handleOnClickDownloadOne = async () => {
    if (Platform.OS !== "web") {
      Alert.alert("Error", "Image download is only available on web.");
      return;
    }

    try {
      if (onDownloadOne && typeof onDownloadOne === "function") {
        onDownloadOne(currentIndex);
      } else {
        const url = images[currentIndex];
        const filename = getDownloadFilename(url);
        try {
          const response = await fetch(url);
          logDownloadDiagnostics("one.fetch", url, response);
          if (!response.ok) throw new Error("Failed to fetch image");
          const blob = await response.blob();
          const blobDownloaded = triggerBlobDownload(blob, filename);
          if (!blobDownloaded) {
            throw new Error("Unable to trigger blob download");
          }
        } catch (error) {
          logDownloadDiagnostics("one.fetch", url, undefined, error);
          const fallbackTriggered = triggerDirectDownloadFallback(url);
          if (!fallbackTriggered) {
            throw new Error("Unable to trigger fallback download");
          }
        }
      }
    } catch (error) {
      Alert.alert("Error", "Unable to download image.");
    }
  };

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === "ArrowRight") {
        handleNext();
      }
      if (event.key === "ArrowLeft") {
        handlePrevious();
      }
      if (event.key === "d" || event.key === "D") {
        handleOnClickDownloadOne();
      }
    };

    // Attach event listener
    window.addEventListener("keydown", handleKeyPress);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [currentIndex]);

  const handleOnClickDeleteOne = () => {
    if (!onDeleteOne) return;
    if (Platform.OS === "web") {
      const shouldDelete = window.confirm(
        "Are you sure you want to delete this image?",
      );
      if (!shouldDelete) return;
      onDeleteOne(currentIndex);
      return;
    }

    Alert.alert("Delete image", "Are you sure you want to delete this image?", [
      {
        text: "Cancel",
        style: "cancel",
      },
      {
        text: "Delete",
        style: "destructive",
        onPress: () => onDeleteOne(currentIndex),
      },
    ]);
  };

  if (images.length === 0)
    return (
      <NormalText
        text="No matches found"
        style={{
          marginTop: 20,
          marginHorizontal: "auto",
        }}
      />
    );

  return (
    <View style={styles.container}>
      {error ? (
        <View
          style={{
            alignItems: "center",
            justifyContent: "center",
            marginTop: 50,
          }}
        >
          <NormalText text={error} style={{ color: tokens.color.error }} />
        </View>
      ) : loading ? (
        <View style={{ alignItems: "center", justifyContent: "center" }}>
          <ActivityIndicator
            style={{ marginTop: 50 }}
            size="large"
            color={tokens.color.loading}
          />
        </View>
      ) : (
        <React.Fragment>
          <View style={styles.imageWrapper}>
            <TouchableOpacity
              style={[styles.button, { opacity: currentIndex === 0 ? 0 : 1 }]}
              onPress={handlePrevious}
              disabled={currentIndex === 0}
            >
              <Arrow direction="left" />
            </TouchableOpacity>

            <View
              style={{
                flex: 1,
                width: "100%",
                height: "100%",
                position: "relative",
              }}
            >
              {images.map((image, index) => (
                <Image
                  key={index}
                  source={{ uri: image }}
                  style={[
                    styles.image,
                    { display: currentIndex === index ? "flex" : "none" },
                    // Apply rotation if available
                    rotations[index] !== undefined
                      ? { transform: [{ rotate: `${rotations[index]}deg` }] }
                      : {},
                  ]}
                />
              ))}
              {showDownloadOneButton && (
                <TouchableOpacity
                  onPress={handleOnClickDownloadOne}
                  testID="gallery-download-one-button"
                  accessibilityRole="button"
                  accessibilityLabel="Download current image"
                  style={{
                    position: "absolute",
                    bottom: 10,
                    right: 5,
                    width: 30,
                    height: 30,
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Download color={tokens.color.text} />
                </TouchableOpacity>
              )}
              {showDeleteButton && onDeleteOne && (
                <TouchableOpacity
                  onPress={handleOnClickDeleteOne}
                  style={styles.deleteOneButton}
                  accessibilityRole="button"
                  accessibilityLabel="Delete current image"
                >
                  <Ionicons
                    name="trash-outline"
                    size={20}
                    color={tokens.color.surface}
                  />
                </TouchableOpacity>
              )}
            </View>

            <TouchableOpacity
              style={[
                styles.button,
                { opacity: currentIndex === images.length - 1 ? 0 : 1 },
              ]}
              onPress={handleNext}
              disabled={currentIndex === images.length - 1}
            >
              <Arrow direction="right" />
            </TouchableOpacity>
          </View>

          {shouldUseCompactNavigation ? (
            <View style={styles.compactNavigationContainer}>
              <View style={styles.compactNavigationRow}>
                <TouchableOpacity
                  onPress={handlePrevious}
                  disabled={!canGoPrevious}
                  style={[
                    styles.navigationTextButton,
                    !canGoPrevious && styles.disabledControl,
                  ]}
                  accessibilityRole="button"
                  accessibilityLabel="Show previous image"
                >
                  <Text style={styles.navigationChevron}>{"<"}</Text>
                </TouchableOpacity>
                <Text
                  style={styles.imageCounter}
                  accessibilityLabel={`Image ${currentIndex + 1} of ${images.length}`}
                >
                  {currentIndex + 1} of {images.length}
                </Text>
                <TouchableOpacity
                  onPress={handleNext}
                  disabled={!canGoNext}
                  style={[
                    styles.navigationTextButton,
                    !canGoNext && styles.disabledControl,
                  ]}
                  accessibilityRole="button"
                  accessibilityLabel="Show next image"
                >
                  <Text style={styles.navigationChevron}>{">"}</Text>
                </TouchableOpacity>
                {shouldShowThumbnailDrawer ? (
                  <TouchableOpacity
                    onPress={() =>
                      setIsThumbnailDrawerOpen((current) => !current)
                    }
                    style={styles.viewAllButton}
                    accessibilityRole="button"
                    accessibilityLabel={
                      isThumbnailDrawerOpen
                        ? "Hide all image thumbnails"
                        : "View all image thumbnails"
                    }
                  >
                    <Text style={styles.viewAllButtonText}>
                      {isThumbnailDrawerOpen ? "Hide all" : "View all"}
                    </Text>
                  </TouchableOpacity>
                ) : null}
              </View>
              {shouldShowThumbnailDrawer && isThumbnailDrawerOpen ? (
                <ScrollView
                  style={styles.thumbnailDrawer}
                  contentContainerStyle={styles.thumbnailGrid}
                >
                  {images.map((item, index) => {
                    const preferredThumbnailUrl = thumbnailUrls[index];
                    const shouldUseFullImage =
                      !preferredThumbnailUrl || failedThumbnailIndexes[index];
                    const thumbnailUrl = shouldUseFullImage
                      ? item
                      : preferredThumbnailUrl;

                    return (
                      <TouchableOpacity
                        key={index}
                        onPress={() => handleSelectImage(index)}
                        style={[
                          styles.drawerThumbnailWrapper,
                          currentIndex === index && styles.activeThumbnail,
                        ]}
                        accessible
                        accessibilityRole="button"
                        accessibilityLabel={`Show image ${index + 1} of ${images.length}`}
                      >
                        <Image
                          source={{ uri: thumbnailUrl }}
                          style={[
                            styles.thumbnailImage,
                            rotations[index] !== undefined
                              ? {
                                  transform: [
                                    { rotate: `${rotations[index]}deg` },
                                  ],
                                }
                              : null,
                          ]}
                          resizeMode="cover"
                          onError={() => {
                            if (!shouldUseFullImage) {
                              setFailedThumbnailIndexes((prev) => ({
                                ...prev,
                                [index]: true,
                              }));
                            }
                          }}
                        />
                        <View style={styles.thumbnailIndexBadge}>
                          <Text style={styles.thumbnailIndexText}>
                            {index + 1}
                          </Text>
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
              ) : null}
            </View>
          ) : showPaginationDots || showpPaginationDots ? (
            <View style={styles.paginationContainer}>
              {images.map((_, index) => (
                <TouchableOpacity
                  onPress={() => handleSelectImage(index)}
                  key={index}
                  style={[
                    styles.dot,
                    currentIndex === index && styles.activeDot,
                  ]}
                  accessibilityRole="button"
                  accessibilityLabel={`Show image ${index + 1} of ${images.length}`}
                />
              ))}
            </View>
          ) : (
            <View
              style={{
                width: "100%",
                maxWidth: 700,
                flexDirection: "row",
                marginHorizontal: "auto",
                justifyContent: "center",
                alignItems: "center",
                paddingVertical: 10,
                // @ts-ignore
                overflowX: "scroll",
                boxSizing: "border-box",
              }}
            >
              {images.map((item, index) => (
                <TouchableOpacity
                  key={index}
                  onPress={() => handleSelectImage(index)}
                  style={[
                    styles.thumbnailWrapper,
                    currentIndex === index && styles.activeThumbnail,
                  ]}
                  accessible
                  accessibilityLabel={`Template ${index + 1}`}
                >
                  <img
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      transform:
                        rotations[index] !== undefined
                          ? `rotate(${rotations[index]}deg)`
                          : "none",
                    }}
                    src={item}
                    alt=""
                  />
                </TouchableOpacity>
              ))}
            </View>
          )}

          {showDownloadAllButton && (
            <PrimaryButton
              text={buttonText}
              width={200}
              roundBorderRadius
              icon={<Download />}
              onPress={handleOnClickDownloadAll}
              style={{
                marginHorizontal: "auto",
                marginTop: "auto",
              }}
            />
          )}
        </React.Fragment>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, position: "relative" },
  imageWrapper: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
  },
  deleteOneButton: {
    position: "absolute",
    top: 10,
    right: 10,
    width: 44,
    height: 44,
    borderRadius: tokens.radius.round,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: tokens.color.dangerSurface,
  },
  image: {
    width: "100%",
    height: "100%",
    shadowColor: tokens.color.shadow,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: tokens.shadow.opacity,
    shadowRadius: tokens.shadow.radius,
    elevation: tokens.shadow.elevation,
    resizeMode: "contain",
    overflow: "visible",
  },
  button: {
    width: 30,
    height: 30,
    borderRadius: 20,
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    justifyContent: "center",
    alignItems: "center",
  },
  paginationContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    paddingVertical: 10,
  },
  compactNavigationContainer: {
    width: "100%",
    maxWidth: tokens.size.compactNavigationMaxWidth,
    marginHorizontal: "auto",
    paddingVertical: tokens.spacing.sm,
    gap: tokens.spacing.sm,
  },
  compactNavigationRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: tokens.spacing.sm,
  },
  navigationTextButton: {
    minHeight: tokens.size.minTouchTarget,
    minWidth: tokens.size.navigationButtonMinWidth,
    paddingHorizontal: tokens.spacing.md,
    borderRadius: tokens.radius.lg,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: tokens.color.surfaceSubtle,
    borderWidth: 1,
    borderColor: tokens.color.border,
  },
  navigationChevron: {
    color: tokens.color.text,
    fontFamily: tokens.font.base,
    fontSize: tokens.fontSize.md,
    lineHeight: tokens.lineHeight.md,
  },
  imageCounter: {
    minHeight: tokens.size.minTouchTarget,
    minWidth: tokens.size.counterMinWidth,
    paddingHorizontal: tokens.spacing.md,
    borderRadius: tokens.radius.lg,
    textAlign: "center",
    textAlignVertical: "center",
    color: tokens.color.text,
    fontFamily: tokens.font.base,
    fontSize: tokens.fontSize.md,
    lineHeight: tokens.lineHeight.md,
  },
  viewAllButton: {
    minHeight: tokens.size.minTouchTarget,
    minWidth: tokens.size.viewAllButtonMinWidth,
    paddingHorizontal: tokens.spacing.md,
    borderRadius: tokens.radius.lg,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: tokens.color.primarySubtle,
    borderWidth: 1,
    borderColor: tokens.color.primary,
  },
  viewAllButtonText: {
    color: tokens.color.text,
    fontFamily: tokens.font.base,
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.lineHeight.sm,
  },
  disabledControl: {
    opacity: 0.45,
  },
  thumbnailDrawer: {
    maxHeight: tokens.size.thumbnailDrawerMaxHeight,
    borderWidth: 1,
    borderColor: tokens.color.border,
    borderRadius: tokens.radius.lg,
    backgroundColor: tokens.color.surfaceSubtle,
  },
  thumbnailGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: tokens.spacing.sm,
    padding: tokens.spacing.sm,
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: 10,
    backgroundColor: tokens.color.borderMuted,
    marginHorizontal: 5,
  },
  activeDot: { width: 14, height: 14 },
  thumbnailWrapper: {
    width: 75,
    height: 75,
    borderRadius: 4,
    overflow: "hidden",
    borderWidth: 2,
    borderColor: tokens.color.transparent,
    flexShrink: 0,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: tokens.color.borderMuted,
    position: "relative",
    marginHorizontal: 5,
  },
  drawerThumbnailWrapper: {
    width: tokens.size.drawerThumbnail,
    height: tokens.size.drawerThumbnail,
    borderRadius: tokens.radius.md,
    overflow: "hidden",
    borderWidth: 2,
    borderColor: tokens.color.transparent,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: tokens.color.borderMuted,
    position: "relative",
  },
  thumbnailImage: {
    width: "100%",
    height: "100%",
  },
  thumbnailIndexBadge: {
    position: "absolute",
    left: tokens.spacing.xs,
    bottom: tokens.spacing.xs,
    minWidth: tokens.size.thumbnailBadge,
    height: tokens.size.thumbnailBadge,
    borderRadius: tokens.radius.lg,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: tokens.color.surface,
    borderWidth: 1,
    borderColor: tokens.color.border,
  },
  thumbnailIndexText: {
    color: tokens.color.text,
    fontFamily: tokens.font.medium,
    fontSize: tokens.fontSize.xs,
    lineHeight: tokens.lineHeight.sm,
  },
  activeThumbnail: { borderColor: tokens.color.primary },
});

export default ImageCarousel;
